/* eslint-disable react-refresh/only-export-components --
 * This module exports both the authoring components (Registers page,
 * CreateRegisterSheet, RegisterDetailSheet — reused by /app/data-entry's
 * Registers sub-tab) AND the shared `cadenceLabel` helper. Co-locating the
 * helper with the components it belongs to is intentional here; the only cost is
 * Vite Fast Refresh HMR for this authoring surface, which is acceptable. */

/**
 * Register authoring — grouped, assignable manual-entry forms (registers).
 *
 * A register is a NAMED, ordered set of manual-entry fields (each a source='manual'
 * tag) assigned to specific operators and filled as ONE form on /app/data-entry — the
 * digital twin of a paper melt log / sand-test register / pour sheet. These are the
 * authoring components (model.write): create a register, add ordered fields, assign the
 * operators who fill it, and review its submission history. They render inside the
 * Registers sub-tab of /app/data-entry (the standalone /app/registers route now redirects
 * there). Filling happens on /app/data-entry (capture.write); submitting writes one
 * reading per field at a shared timestamp, so dashboards / batches / correlations pick
 * the values up like any datapoint.
 *
 * Backend: internal/twin/registers.go (migration 0073). API module: lib/api/registers.ts.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardList,
  History,
  ListChecks,
  Loader2,
  Lock,
  Plus,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Card, CardContent } from "@/lib/ui/card";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { Badge } from "@/lib/ui/badge";
import { EmptyState } from "@/lib/ui/empty-state";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { ConfirmDialog } from "@/lib/ui/confirm-dialog";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";
import {
  backendErrorMessage,
  membersApi,
  registers,
  twinBindings,
  twinEntities,
  type Member,
  type Register,
  type RegisterCadence,
  type SubmissionValue,
} from "@/lib/api";

// The four hand-keyed datapoint kinds a register field may take (mirrors the
// backend's manualTagKinds gate + the equipment ManualEntryTagsPanel).
const FIELD_KINDS: { value: string; label: string }[] = [
  { value: "analog_scalar", label: "Number" },
  { value: "analog_percentage", label: "Percent" },
  { value: "count_scalar", label: "Count" },
  { value: "string_label", label: "Text" },
];

const CADENCES: { value: RegisterCadence; label: string }[] = [
  { value: "per_shift", label: "Per shift" },
  { value: "per_heat", label: "Per heat" },
  { value: "per_batch", label: "Per batch" },
  { value: "adhoc", label: "Ad-hoc" },
];

const SELECT_CLASS =
  "h-9 rounded-md border border-border bg-surface px-2 text-sm text-on-surface";

// Common engineering units offered as datalist suggestions on the field form — mirrors
// the equipment Manual-entry tags panel's list so register authoring feels the same.
// Free-text is still allowed (it's a datalist, not a fixed select).
const MANUAL_UNITS = ["°C", "kg", "%", "nos", "mm", "bar", "rpm", "kW", "ppm", "pH", "L", "sec"];

export function cadenceLabel(c: string): string {
  return CADENCES.find((x) => x.value === c)?.label ?? c;
}

export function Registers() {
  const { can } = useOrg();
  const canWrite = can("model.write");
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ["registers"],
    queryFn: () => registers.list(),
    enabled: canWrite,
    staleTime: 30_000,
  });
  const items = listQ.data?.items ?? [];

  if (!canWrite) {
    return (
      <PageContainer className="max-w-3xl">
        <PageHeader
          title="Registers"
          description="Author grouped manual-entry forms your operators fill on the floor."
        />
        <Card className="mt-6">
          <CardContent className="flex items-start gap-3 py-6">
            <Lock className="mt-0.5 h-5 w-5 shrink-0 text-on-surface-muted" />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-on-surface">
                You don&apos;t have access to author registers
              </p>
              <p className="text-sm text-on-surface-muted">
                Creating and assigning registers needs the{" "}
                <span className="font-medium">model.write</span> permission — the Member
                or Admin role grants it. If a register has been assigned to you, fill it on{" "}
                <span className="font-medium">Data entry</span>.
              </p>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="max-w-4xl">
      <PageHeader
        title="Registers"
        description="A register is a named, ordered set of manual-entry fields assigned to specific operators and filled as one form — the digital twin of a paper melt log, sand-test register or pour sheet. Values flow through the same pipeline as any datapoint."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1.5 size-4" />
            New register
          </Button>
        }
      />

      <div className="mt-6 flex flex-col gap-3">
        {listQ.isLoading && (
          <p className="text-sm text-on-surface-muted">Loading registers…</p>
        )}

        {!listQ.isLoading && items.length === 0 && (
          <EmptyState
            icon={ClipboardList}
            title="No registers yet"
            description="Create a register, add the fields your operators record (pour temperature, sand moisture, moulds made), and choose who fills it in. It then shows on their Data entry screen as one form."
            cta={
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-1.5 size-4" />
                New register
              </Button>
            }
          />
        )}

        {items.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setOpenId(r.id)}
            className="flex items-center justify-between gap-4 rounded-lg border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-on-surface-muted hover:bg-surface-elevated"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-on-surface">
                  {r.name}
                </span>
                <Badge variant="secondary" className="text-[10px] uppercase">
                  {cadenceLabel(r.cadence)}
                </Badge>
              </div>
              {r.description && (
                <span className="truncate text-xs text-on-surface-muted">
                  {r.description}
                </span>
              )}
            </div>
            <span className="shrink-0 text-xs text-on-surface-muted">
              {r.field_count} field{r.field_count === 1 ? "" : "s"}
            </span>
          </button>
        ))}
      </div>

      {createOpen && (
        <CreateRegisterSheet
          onClose={() => setCreateOpen(false)}
          onCreated={(reg) => {
            setCreateOpen(false);
            setOpenId(reg.id);
          }}
        />
      )}
      {openId && (
        <RegisterDetailSheet registerId={openId} onClose={() => setOpenId(null)} />
      )}
    </PageContainer>
  );
}

// ── Create ──────────────────────────────────────────────────────────────────

export function CreateRegisterSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (reg: Register) => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cadence, setCadence] = useState<RegisterCadence>("per_shift");
  const [entityId, setEntityId] = useState("");

  // Optional equipment scope — an equipment-scoped register also binds its fields
  // to that entity's dashboard. Fetched lazily while the sheet is open.
  const entitiesQ = useQuery({
    queryKey: ["twin-entities", "for-register"],
    queryFn: () => twinEntities.list({ limit: 200 }),
    staleTime: 60_000,
  });
  const entities = entitiesQ.data?.items ?? [];

  const createMut = useMutation({
    mutationFn: () =>
      registers.create({
        name: name.trim(),
        description: description.trim() || undefined,
        cadence,
        entity_id: entityId || undefined,
      }),
    onSuccess: (reg) => {
      void qc.invalidateQueries({ queryKey: ["registers"] });
      toast.push("ok", `Register “${reg.name}” created — add its fields next.`);
      onCreated(reg);
    },
    onError: (err) =>
      toast.push(
        "error",
        backendErrorMessage(err) || "Couldn't create the register.",
      ),
  });

  return (
    <Sheet open onClose={onClose} widthClass="max-w-md" ariaLabel="New register">
      <SheetHeader
        title="New register"
        subtitle="Name it, then add fields and assign operators."
        onClose={onClose}
      />
      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reg-name">Name</Label>
          <Input
            id="reg-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Melt shop shift log"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reg-desc">Description (optional)</Label>
          <Input
            id="reg-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this register records"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reg-cadence">Cadence</Label>
          <select
            id="reg-cadence"
            className={SELECT_CLASS}
            value={cadence}
            onChange={(e) => setCadence(e.target.value as RegisterCadence)}
          >
            {CADENCES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reg-entity">Equipment (optional)</Label>
          <select
            id="reg-entity"
            className={SELECT_CLASS}
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
          >
            <option value="">Not equipment-specific</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-on-surface-muted">
            Scoping to an equipment also shows the register&apos;s fields on that
            equipment&apos;s dashboard.
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-border p-5">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => createMut.mutate()}
          disabled={!name.trim() || createMut.isPending}
        >
          {createMut.isPending && <Loader2 className="mr-1.5 size-4 animate-spin" />}
          Create register
        </Button>
      </div>
    </Sheet>
  );
}

// ── Detail ──────────────────────────────────────────────────────────────────

export function RegisterDetailSheet({
  registerId,
  onClose,
}: {
  registerId: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const detailKey = ["register", registerId];
  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () => registers.get(registerId),
  });
  const reg = detailQ.data;

  const deleteMut = useMutation({
    mutationFn: () => registers.remove(registerId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["registers"] });
      toast.push("ok", "Register deleted.");
      onClose();
    },
    onError: (err) =>
      toast.push("error", backendErrorMessage(err) || "Couldn't delete the register."),
  });

  return (
    <Sheet open onClose={onClose} widthClass="max-w-2xl" ariaLabel="Register detail">
      <SheetHeader
        title={reg?.name ?? "Register"}
        subtitle={reg ? `${cadenceLabel(reg.cadence)} · ${reg.field_count} field${reg.field_count === 1 ? "" : "s"}` : undefined}
        onClose={onClose}
        actions={
          <Button
            variant="ghost"
            size="icon"
            aria-label="Delete register"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="size-4 text-danger" />
          </Button>
        }
      />

      {detailQ.isLoading && (
        <p className="p-5 text-sm text-on-surface-muted">Loading register…</p>
      )}
      {detailQ.isError && (
        <p className="p-5 text-sm text-danger">Couldn&apos;t load this register.</p>
      )}

      {reg && (
        <div className="flex flex-1 flex-col gap-6 p-5">
          <FieldsEditor register={reg} detailKey={detailKey} />
          <AssigneesEditor register={reg} detailKey={detailKey} />
          <SubmissionsSection registerId={registerId} />
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => {
          if (!deleteMut.isPending) setConfirmDelete(false);
        }}
        onConfirm={() => deleteMut.mutate()}
        title="Delete this register?"
        description="This removes the register, its fields and assignments, and its submission history. The datapoints already recorded from past submissions are kept. This can't be undone."
        confirmLabel="Delete register"
        destructive
      />
    </Sheet>
  );
}

// ── Fields editor ─────────────────────────────────────────────────────────────

function FieldsEditor({
  register,
  detailKey,
}: {
  register: Register;
  detailKey: (string | undefined)[];
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("");
  const [kind, setKind] = useState("analog_scalar");

  const entityId = register.entity_id || "";
  const fields = useMemo(() => register.fields ?? [], [register.fields]);

  // The equipment's existing manual-entry tags (source='manual' bindings) — the same
  // list its Manual-entry tags panel shows. Drives the field typeahead so an operator
  // reuses an existing datapoint instead of minting a duplicate. Equipment-scoped only.
  const bindingsQ = useQuery({
    queryKey: ["twin-bindings", entityId],
    queryFn: () => twinBindings.list(entityId),
    enabled: !!entityId,
    staleTime: 30_000,
  });
  const onRegister = useMemo(() => new Set(fields.map((f) => f.tag_id)), [fields]);
  // Manual tags on this equipment not already on the register (image tags excluded —
  // a register field is a keyed value, not a photo).
  const existingTags = useMemo(
    () =>
      (bindingsQ.data?.items ?? [])
        .filter(
          (b) =>
            b.source === "manual" && b.data_kind !== "image" && !onRegister.has(b.tag_id),
        )
        .map((b) => ({ tagId: b.tag_id, name: b.tag_name, unit: b.unit, dataKind: b.data_kind })),
    [bindingsQ.data, onRegister],
  );
  // Typed name matches an available existing manual tag → reuse it (never duplicate).
  const matched = useMemo(() => {
    const n = name.trim().toLowerCase();
    return n ? existingTags.find((t) => t.name.toLowerCase() === n) ?? null : null;
  }, [name, existingTags]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: detailKey });
    void qc.invalidateQueries({ queryKey: ["registers"] });
    // Keep the org-wide datapoint catalogue fresh (a new field binds a tag).
    void qc.invalidateQueries({ queryKey: ["org-tags"] });
    // Reflect the (possibly newly-bound) tag in the equipment's Manual-entry panel live.
    // Every bindings read, the org-wide one too, which greys out a machine with no tag (#1236 review, MEDIUM).
    if (entityId) void qc.invalidateQueries({ queryKey: ["twin-bindings"] });
  };

  const addMut = useMutation({
    mutationFn: () =>
      registers.addField(
        register.id,
        matched
          ? { tag_id: matched.tagId, sort_order: fields.length }
          : {
              name: name.trim(),
              unit: unit.trim() || undefined,
              data_kind: kind,
              sort_order: fields.length,
            },
      ),
    onSuccess: () => {
      setName("");
      setUnit("");
      setKind("analog_scalar");
      invalidate();
      toast.push("ok", matched ? "Existing datapoint added." : "Field added.");
    },
    onError: (err) =>
      toast.push("error", backendErrorMessage(err) || "Couldn't add the field."),
  });

  const removeMut = useMutation({
    mutationFn: (fieldId: string) => registers.removeField(register.id, fieldId),
    onSuccess: () => {
      invalidate();
      toast.push("ok", "Field removed.");
    },
    onError: (err) =>
      toast.push("error", backendErrorMessage(err) || "Couldn't remove the field."),
  });

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <ListChecks className="size-4 text-on-surface-muted" />
        <h3 className="text-sm font-semibold text-on-surface">Fields</h3>
        <span className="text-xs text-on-surface-muted">
          the values operators record, in order
        </span>
      </div>

      {fields.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-on-surface-muted">
          No fields yet — add the first one below.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {fields.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm text-on-surface">
                  {f.label}
                  {f.unit ? (
                    <span className="text-on-surface-muted"> ({f.unit})</span>
                  ) : null}
                </span>
                <span className="truncate text-[11px] text-on-surface-muted">
                  {f.metric}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant="outline" className="text-[10px] uppercase">
                  {FIELD_KINDS.find((k) => k.value === f.data_kind)?.label ?? f.data_kind}
                </Badge>
                <button
                  type="button"
                  aria-label={`Remove ${f.label}`}
                  onClick={() => removeMut.mutate(f.id)}
                  disabled={removeMut.isPending}
                  className="rounded p-1 text-on-surface-muted hover:bg-surface-elevated hover:text-danger"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="field-name" className="text-xs">
              Field
            </Label>
            <Input
              id="field-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Pour temp"
              list={entityId ? "register-existing-tags" : undefined}
              className="h-9 w-44"
            />
            {entityId && (
              <datalist id="register-existing-tags">
                {existingTags.map((t) => (
                  <option key={t.tagId} value={t.name} />
                ))}
              </datalist>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="field-unit" className="text-xs">
              Unit
            </Label>
            <Input
              id="field-unit"
              value={matched ? matched.unit : unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="°C"
              list="register-units"
              disabled={!!matched}
              className="h-9 w-24"
            />
            <datalist id="register-units">
              {MANUAL_UNITS.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="field-kind" className="text-xs">
              Type
            </Label>
            <select
              id="field-kind"
              className={SELECT_CLASS}
              value={matched ? matched.dataKind : kind}
              onChange={(e) => setKind(e.target.value)}
              disabled={!!matched}
            >
              {FIELD_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
          <Button
            size="sm"
            onClick={() => addMut.mutate()}
            disabled={!name.trim() || addMut.isPending}
          >
            {addMut.isPending ? (
              <Loader2 className="mr-1.5 size-4 animate-spin" />
            ) : (
              <Plus className="mr-1.5 size-4" />
            )}
            Add field
          </Button>
        </div>
        {name.trim() ? (
          <p className="text-[11px] text-on-surface-muted">
            {matched ? (
              <>
                Reusing this equipment&apos;s existing datapoint{" "}
                <span className="font-medium text-on-surface">{matched.name}</span>
                {matched.unit ? ` (${matched.unit})` : ""} — one shared tag, no duplicate.
              </>
            ) : entityId ? (
              <>
                New datapoint — also added to this equipment&apos;s Manual-entry tags. Pick
                a suggestion to reuse an existing one instead.
              </>
            ) : (
              "New datapoint."
            )}
          </p>
        ) : entityId && existingTags.length > 0 ? (
          <p className="text-[11px] text-on-surface-muted">
            Tip: start typing to reuse one of this equipment&apos;s {existingTags.length}{" "}
            existing datapoint{existingTags.length === 1 ? "" : "s"}, or type a new name.
          </p>
        ) : null}
      </div>
    </section>
  );
}

// ── Assignees editor ──────────────────────────────────────────────────────────

function AssigneesEditor({
  register,
  detailKey,
}: {
  register: Register;
  detailKey: (string | undefined)[];
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { activeOrgId } = useOrg();
  const [pick, setPick] = useState("");

  const membersQ = useQuery({
    queryKey: ["org-members", activeOrgId],
    queryFn: () => membersApi.list(activeOrgId as string),
    enabled: !!activeOrgId,
    staleTime: 60_000,
  });

  const assigned = useMemo(() => register.assignees ?? [], [register.assignees]);
  const assignedIds = useMemo(
    () => new Set(assigned.map((a) => a.user_id)),
    [assigned],
  );
  const candidates: Member[] = useMemo(
    () => (membersQ.data?.items ?? []).filter((m) => !assignedIds.has(m.user_id)),
    [membersQ.data, assignedIds],
  );

  const invalidate = () => void qc.invalidateQueries({ queryKey: detailKey });

  const addMut = useMutation({
    mutationFn: (userId: string) => registers.addAssignee(register.id, userId),
    onSuccess: () => {
      setPick("");
      invalidate();
      toast.push("ok", "Operator assigned.");
    },
    onError: (err) =>
      toast.push("error", backendErrorMessage(err) || "Couldn't assign the operator."),
  });

  const removeMut = useMutation({
    mutationFn: (userId: string) => registers.removeAssignee(register.id, userId),
    onSuccess: () => {
      invalidate();
      toast.push("ok", "Operator unassigned.");
    },
    onError: (err) =>
      toast.push("error", backendErrorMessage(err) || "Couldn't unassign the operator."),
  });

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Users className="size-4 text-on-surface-muted" />
        <h3 className="text-sm font-semibold text-on-surface">Assigned operators</h3>
        <span className="text-xs text-on-surface-muted">who fills this register</span>
      </div>

      {assigned.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-on-surface-muted">
          Nobody assigned yet — assigned operators see this register on their Data entry
          screen.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {assigned.map((a) => (
            <li
              key={a.user_id}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2"
            >
              <span className="truncate text-sm text-on-surface">
                {a.display_name ? `${a.display_name} · ${a.email}` : a.email}
              </span>
              <button
                type="button"
                aria-label={`Unassign ${a.email}`}
                onClick={() => removeMut.mutate(a.user_id)}
                disabled={removeMut.isPending}
                className="rounded p-1 text-on-surface-muted hover:bg-surface-elevated hover:text-danger"
              >
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-end gap-2">
        <select
          aria-label="Operator to assign"
          className={`${SELECT_CLASS} min-w-0 flex-1`}
          value={pick}
          onChange={(e) => setPick(e.target.value)}
        >
          <option value="">
            {candidates.length === 0 ? "No more members to assign" : "Select an operator…"}
          </option>
          {candidates.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.display_name ? `${m.display_name} (${m.email})` : m.email}
              {" · "}
              {m.role}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          onClick={() => pick && addMut.mutate(pick)}
          disabled={!pick || addMut.isPending}
        >
          {addMut.isPending && <Loader2 className="mr-1.5 size-4 animate-spin" />}
          Assign
        </Button>
      </div>
    </section>
  );
}

// ── Submission history ────────────────────────────────────────────────────────

// A numeric kind shows `value` (+ unit if present); a text kind shows `value_text`.
// Returns null for an empty value so callers can skip it. Mirrors the data_kind
// routing used when the fields are filled on Data entry.
function formatSubmissionValue(v: SubmissionValue): string | null {
  const numeric =
    v.data_kind.includes("scalar") ||
    v.data_kind.includes("percentage") ||
    v.data_kind.includes("numeric") ||
    v.data_kind.includes("integer");
  if (numeric) {
    if (v.value === null) return null;
    return `${v.label} ${v.value}${v.unit ? ` ${v.unit}` : ""}`;
  }
  const text = v.value_text.trim();
  if (text === "") return null;
  return `${v.label} ${text}`;
}

function SubmissionsSection({ registerId }: { registerId: string }) {
  const subsQ = useQuery({
    queryKey: ["register-submissions", registerId],
    queryFn: () => registers.submissions(registerId),
    staleTime: 15_000,
  });
  const subs = subsQ.data?.items ?? [];

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <History className="size-4 text-on-surface-muted" />
        <h3 className="text-sm font-semibold text-on-surface">Recent submissions</h3>
      </div>

      {subsQ.isLoading ? (
        <p className="text-xs text-on-surface-muted">Loading history…</p>
      ) : subs.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-on-surface-muted">
          No submissions yet. Fills logged on Data entry appear here.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {subs.map((s) => {
            const formatted = (s.values ?? [])
              .map(formatSubmissionValue)
              .filter((x): x is string => x !== null);
            return (
              <li
                key={s.id}
                className="flex flex-col gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-on-surface">
                      {s.submitted_by_name || "—"}
                    </span>
                    {s.note && (
                      <span className="truncate text-[11px] text-on-surface-muted">
                        {s.note}
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-xs text-on-surface-muted">
                    <span>
                      {s.value_count} value{s.value_count === 1 ? "" : "s"}
                    </span>
                    <span>·</span>
                    <span>{new Date(s.submitted_at).toLocaleString()}</span>
                  </div>
                </div>
                {formatted.length > 0 && (
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-on-surface-muted">
                    {formatted.map((text, i) => (
                      <span key={i}>
                        {i > 0 && <span className="mr-2 text-border">·</span>}
                        <span className="text-on-surface">{text}</span>
                      </span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
